import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TransactionDashboard from './TransactionDashboard';

const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    try {
      // Calls server which clears the HttpOnly admin_token cookie.
      await axios.post('/api/auth/logout', {}, { withCredentials: true });
    } catch (err) {
      // Even if the server call fails, drop client UI state and redirect.
    }
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setError('');
      // credentials: 'include' so the HttpOnly admin_token cookie is sent.
      const response = await fetch('/api/admin/products', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to fetch products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (productId) => {
    navigate(`/admin/products/${productId}`);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const response = await fetch(`/api/admin/products/${productId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Delete failed');
        }
        fetchProducts();
      } catch (err) {
        setError('Failed to delete product');
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <div className="tab-buttons">
            <button 
              className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              Products
            </button>
            <button 
              className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('transactions')}
            >
              Transactions
            </button>
          </div>
          {activeTab === 'products' && (
            <button onClick={() => navigate('/admin/products/new')} className="add-button">
              Add New Product
            </button>
          )}
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {error && <div className="error" style={{ marginBottom: 16 }}>{error}</div>}
        {activeTab === 'products' ? (
          <>
            <section className="products-section">
              <h2>Products</h2>
              <div className="products-grid">
                {products.map((product) => (
                  <div key={product._id} className="product-card">
                    <img src={product.image} alt={product.name} />
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <p>{product.description}</p>
                      <div className="product-price">${product.price}</div>
                      <div className="product-actions">
                        <button onClick={() => handleEdit(product._id)} className="edit-button">
                          Edit
                        </button>
                        <button onClick={() => handleDelete(product._id)} className="delete-button">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="quick-stats">
              <h2>Quick Stats</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Total Products</h3>
                  <p>{products.length}</p>
                </div>
                <div className="stat-card">
                  <h3>Active Products</h3>
                  <p>{products.filter(p => p.active).length}</p>
                </div>
              </div>
            </section>
          </>
        ) : (
          <TransactionDashboard />
        )}
      </div>

      <style>{`
        .admin-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        .header-actions {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .tab-buttons {
          display: flex;
          gap: 10px;
        }
        .tab-button {
          padding: 10px 20px;
          background: #f0f0f0;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.3s;
        }
        .tab-button.active {
          background: #007bff;
          color: white;
        }
        .tab-button:hover:not(.active) {
          background: #e0e0e0;
        }
        .add-button {
          padding: 10px 20px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
        }
        .logout-button {
          padding: 10px 20px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: background 0.3s;
        }
        .logout-button:hover {
          background: #c82333;
        }
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }
        .product-card {
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        .product-card img {
          width: 100%;
          height: 200px;
          object-fit: cover;
        }
        .product-info {
          padding: 20px;
        }
        .product-price {
          font-size: 1.2em;
          font-weight: bold;
          color: #28a745;
          margin: 10px 0;
        }
        .product-actions {
          display: flex;
          gap: 10px;
          margin-top: 15px;
        }
        .edit-button, .delete-button {
          padding: 8px 15px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
        }
        .edit-button {
          background: #007bff;
          color: white;
        }
        .delete-button {
          background: #dc3545;
          color: white;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }
        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
        }
        .stat-card h3 {
          color: #666;
          margin-bottom: 10px;
        }
        .stat-card p {
          font-size: 2em;
          font-weight: bold;
          color: #007bff;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard; 